// The season numbers the sim reads (PLAN-01 Stream D). Prices, flock sizes and
// the Veteran cooldown belong to the season, never to code (REFOCUS §11b).
//
// The sim only ever sees the bundled defaults here: it has to run with no
// network (offline catch-up, tests, the future server replay), and a locked
// season's config is the same shape. `net/seasonConfig.ts` owns fetching and
// verifying a live season; this file is the synchronous, DOM-free read the
// simulation is allowed to make.

import type { Breed } from '../core/types';
import defaults from '../data/season-defaults.json';
import type { SeasonBagSize, SeasonConfig } from '../net/seasonConfig.schema';

/** Season id the bundled defaults play under. `net/seasonConfig.ts` uses it too. */
export const BUNDLED_SEASON_ID = 'bundled-defaults';

const BUNDLED = defaults.config as unknown as SeasonConfig;

// --- installing a live season ---------------------------------------------
//
// The sim is synchronous and has to work with no network, so it reads through
// this module and nothing else. `net/seasonConfig.ts` owns the awkward part —
// fetching, hashing, refusing a config it cannot verify — and hands the result
// over once, at boot. Everything after that is a plain synchronous read.

export type InstalledSource = 'network' | 'cache' | 'defaults';

/** What `installSeason` needs. Structural, so `sim/` never imports `net/`. */
export interface InstallableSeason {
  id: string;
  config: SeasonConfig;
  /** Empty for a season with no locked hash, which means the bundle. */
  configHash: string;
  source: InstalledSource;
}

let installed: InstallableSeason | null = null;

/** The season config in force for the sim. */
export function seasonConfig(): SeasonConfig {
  return installed?.config ?? BUNDLED;
}

/** The season the numbers in force came from, for the save's season stamp. */
export function activeSeasonId(): string {
  return installed?.id ?? BUNDLED_SEASON_ID;
}

export function activeSeasonHash(): string {
  return installed?.configHash ?? '';
}

export function activeSeasonSource(): InstalledSource {
  return installed?.source ?? 'defaults';
}

/**
 * Puts a verified season in force; `null` goes back to the bundle.
 *
 * Installing is not a price change, it is a change of which league the save
 * belongs to, so the caller decides whether it is safe — see
 * `shouldAdoptSeason`. The bundle stays in memory as the fallback rather than
 * being swapped out, so a failed install is always one call away.
 */
export function installSeason(season: InstallableSeason | null): void {
  installed = season;
}

/**
 * Whether the season on offer is the one this save should play under.
 *
 * A save with no stamp is free to take any season. A save that already carries
 * one has been stamped for a reason: the league seed is derived from the season
 * id, and the server replay (REFOCUS §11) scores the action log against the
 * config the stamp names. Re-pointing a running Veteran save at a new season
 * would change its seed mid-run and put the numbers on screen out of step with
 * the numbers its stamp promises — so it keeps the season it started.
 *
 * Cost of that rule: a player who stays in a run started last season keeps last
 * season's prices until the run ends. That is the honest failure, because the
 * alternative is a run whose replay no longer matches what was played.
 */
export function shouldAdoptSeason(save: { season?: { seasonId: string } }): boolean {
  return !save.season;
}

/**
 * Eggits for an item this season, or `fallback` when the season does not price
 * it. The fallback is the catalog price in items.json, which is what the store
 * charged before seasons existed.
 */
export function seasonPrice(itemId: string, fallback: number): number {
  const price = seasonConfig().prices[itemId];
  return typeof price === 'number' ? price : fallback;
}

/** Straight-run price of one chick of `breed` (REFOCUS §5). */
export function seasonChickPrice(breed: Breed): number {
  const { common, byBreed } = seasonConfig().chickPrices;
  return byBreed[breed] ?? common;
}

/** What the opening basket may spend on chicks, in Eggits (REFOCUS §5). */
export function seasonChickBudget(): number {
  return seasonConfig().chickBudget;
}

/**
 * The purse a new save opens with, in Eggits (REFOCUS §11b).
 *
 * The season's number rather than a constant, because a Veteran save's league
 * replay runs against the config its stamp names: a purse typed into the save
 * builder would hand a run money the replay never accounted for.
 */
export function seasonStartEggits(): number {
  return seasonConfig().startEggits;
}

/** What a sexed chick costs over its straight-run price. */
export function seasonSexedMultiplier(): number {
  return seasonConfig().sexedChickMultiplier;
}

/** The share of sexed chicks that are hens — the buy screen's "~90% hens". */
export function seasonSexedHenRate(): number {
  return seasonConfig().sexedHenRate;
}

/**
 * Hours a Veteran bird has to be left alone between warnings. The number is the
 * season's (REFOCUS §11b, §3) and was a 12-hour draft (REFOCUS §17); a season
 * locked in the admin panel moves it.
 */
export function seasonVeteranWarningCooldownHours(): number {
  return seasonConfig().veteranWarningCooldownHours;
}

/**
 * The feed bags the store sells. Copied rather than handed over, because the
 * installed config is the season the save is stamped with and a screen sorting
 * these in place would edit it.
 */
export function seasonBagSizes(): SeasonBagSize[] {
  return seasonConfig().bagSizes.map((bag) => ({ ...bag }));
}
